import React, { useContext, useEffect, useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Dialog,
  Button,
  FormControl,
  Switch,
  Select,
  MenuItem,
  Typography,
  DialogContent,
  FormHelperText,
  TextField,
} from "@mui/material";

import "../../Style/dialogstyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import useOptionPriceForm from "./Hooks/useOptionPriceForm";
import {
  SelectMultiLesson,
  SelectPackagePriceByClass,
} from "../Include/DynamicSelect/DynamicSelect";

export default function OptionPriceForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  classId,
  hideBackdrop = false,
}) {
  const { t } = useContext(AuthContext);
  const [price, setPrice] = useState(0);

  const PAY_STATUS = [
    { value: "pay", label: t("thead_pay") || "Pay" },
    { value: "free", label: t("thead_free") || "Free" },
  ];

  const validationSchema = Yup.object().shape({
    accessedLessonIds: Yup.array()
      .of(Yup.string())
      .min(1, t("required") || "Required"),
    packagePriceId: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
    // packagepriceIds: Yup.array()
    //   .of(Yup.string())
    //   .min(1, t("required") || "Required"),
    payStatus: Yup.string().required(t("required") || "Required"),
    default: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      packagePriceId: { id: "", title: "" },
      accessedLessonIds: [],
      // packagepriceIds: [],
      payStatus: "pay",
      default: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      const input = {
        accessedLessonIds: values.accessedLessonIds,
        packagepriceIds: [values.packagePriceId.id],
        payStatus: values.payStatus,
        default: Boolean(values.default),
      };

      // console.log("input::", input)

      if (dialogTitle === "Create") {
        createOptionPrice({ variables: { input } });
      } else {
        updateOptionPrice({
          variables: {
            id: editData?._id,
            input,
          },
        });
      }
    },
  });

  const {
    errors,
    values,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    getFieldProps,
  } = formik;

  // console.log("values::", values);

  const { createOptionPrice, updateOptionPrice, loading, setLoading } =
    useOptionPriceForm({
      resetForm,
      setRefetch,
      handleClose,
    });

  useEffect(() => {
    if (open) {
      if (dialogTitle === "Update" && editData) {
        const formatIds = (items) => {
          if (!Array.isArray(items)) return [];
          return items.map((item) =>
            typeof item === "object" && item !== null
              ? item._id || item.id
              : item,
          );
        };

        setFieldValue("packagePriceId.id", editData?.packagepriceIds[0]?._id);
        setFieldValue(
          "packagePriceId.title",
          `${editData?.packagepriceIds[0]?.title} - ${editData?.packagepriceIds[0]?.groupFeeId.price} $`,
        );
        setFieldValue(
          "accessedLessonIds",
          formatIds(editData.accessedLessonIds),
        );
        setPrice(editData?.packagepriceIds[0]?.groupFeeId.price);
        setFieldValue("payStatus", editData.payStatus || "pay");
        setFieldValue("default", Boolean(editData.default));
      } else if (dialogTitle === "Create") {
        resetForm();
      }
    }
  }, [open, dialogTitle, editData]);

  // console.log("editData::", editData?.packagepriceIds[0]?.groupFeeId.price);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="md"
      fullWidth
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_option_price")
            : t("modal_title_update_option_price")
        }
        classTitle="dialog-title"
        classIcon="close-icon"
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("page_package_price")}
                </Typography>
                <SelectPackagePriceByClass
                  classId={classId}
                  value={values?.packagePriceId}
                  onChange={(val) => {
                    if (val) {
                      setFieldValue("packagePriceId", {
                        id: val.id,
                        title: val.title,
                      });
                      setPrice(val.price || 0);
                      // console.log("Selected packagePriceId:", val);
                    } else {
                      setFieldValue("packagePriceId.id", "");
                      setFieldValue("packagePriceId.title", "");
                    }
                  }}
                />
                {Boolean(
                  errors.packagePriceId?.id && touched.packagePriceId?.id,
                ) && (
                  <FormHelperText error id="content-id-error">
                    {errors.packagePriceId?.id}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} sm={3}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_amount")} ($)
                </Typography>
                <TextField
                  disabled
                  className="text-field"
                  size="small"
                  type="number"
                  fullWidth
                  value={
                    values?.packagePriceId?.id
                      ? price
                      : editData?.packagepriceIds[0]?.groupFeeId.price
                        ? editData?.packagepriceIds[0]?.groupFeeId.price
                        : 0
                  }
                  placeholder={t("thead_amount") || "Amount"}
                />
              </Grid>

              {/* Pay Status */}
              <Grid item xs={12} sm={3}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_pay_status")}
                </Typography>
                <FormControl
                  fullWidth
                  size="small"
                  error={Boolean(touched.payStatus && errors.payStatus)}
                >
                  <Select {...getFieldProps("payStatus")}>
                    {PAY_STATUS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.payStatus && errors.payStatus && (
                    <FormHelperText>{errors.payStatus}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={8}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_price")}{" "}
                  {values?.packagePriceId?.id
                    ? price
                    : editData?.packagepriceIds[0]?.groupFeeId.price
                      ? editData?.packagepriceIds[0]?.groupFeeId.price
                      : 0}
                  {" $ "}
                  {t("thead_accessed_lesson")}
                </Typography>
                <SelectMultiLesson
                  classId={classId}
                  value={values.accessedLessonIds}
                  onChange={(val) => {
                    if (Array.isArray(val)) {
                      setFieldValue(
                        "accessedLessonIds",
                        val.map((item) =>
                          typeof item === "object" ? item._id || item.id : item,
                        ),
                      );
                    } else {
                      setFieldValue("accessedLessonIds", []);
                    }
                  }}
                />
                {Boolean(
                  touched.accessedLessonIds && errors.accessedLessonIds,
                ) && (
                  <FormHelperText error>
                    {typeof errors.accessedLessonIds === "string"
                      ? errors.accessedLessonIds
                      : t("required")}
                  </FormHelperText>
                )}
              </Grid>

              {/* Default Switch */}
              <Grid item xs={12} sm={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_default")}
                </Typography>
                <Switch
                  checked={values.default}
                  onChange={(e) => setFieldValue("default", e.target.checked)}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  className="btn-create"
                  disabled={loading}
                >
                  {loading
                    ? t("loading")
                    : dialogTitle === "Update"
                      ? t("update")
                      : t("create")}
                </Button>
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
